import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import ScrollReveal, { ScrollStaggerContainer } from '@/components/base/ScrollReveal';

export default function FeaturedProducts() {
  const { t } = useTranslation();

  const products = [
    { titleKey: 'products.sellMore', priceKey: 'products.sellMorePrice', descKey: 'products.sellMoreDesc', icon: 'ri-store-2-line', href: '/sell-more-workshop', featured: false, ctaKey: 'products.buyNow' },
    { titleKey: 'products.prompts', priceKey: 'products.promptsPrice', descKey: 'products.promptsDesc', icon: 'ri-file-text-line', href: '/advertising-prompts', featured: false, ctaKey: 'products.buyNow' },
    { titleKey: 'products.phoenix', priceKey: 'products.phoenixPrice', descKey: 'products.phoenixDesc', icon: 'ri-fire-line', href: '/phoenix-lite-mentoring', featured: true, ctaKey: 'products.joinNow' },
    { titleKey: 'products.trafficPro', priceKey: 'products.trafficProPrice', descKey: 'products.trafficProDesc', icon: 'ri-rocket-line', href: '/traffic-pro', featured: false, ctaKey: 'products.hireNow' },
    { titleKey: 'products.trafficMaster', priceKey: 'products.trafficMasterPrice', descKey: 'products.trafficMasterDesc', icon: 'ri-vip-crown-line', href: '/traffic-master', featured: false, ctaKey: 'products.bookConsultation' },
    { titleKey: 'products.webDesign', priceKey: 'products.webDesignPrice', descKey: 'products.webDesignDesc', icon: 'ri-macbook-line', href: '/web-design', featured: false, ctaKey: 'products.viewPlans' },
  ];

  return (
    <section id="products" className="py-20 md:py-28 bg-white overflow-hidden">
      <div className="mx-auto max-w-[1200px] px-6 md:px-10">
        <ScrollReveal direction="up" duration={0.7}>
          <div className="mb-14 md:mb-18">
            <span className="inline-block px-4 py-1.5 rounded-full border border-red-200 bg-red-50 text-red-600 text-xs font-semibold tracking-wider uppercase mb-5">
              {t('products.tag')}
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
              {t('products.title')}{' '}
              <span className="text-primary-500">{t('products.titleHighlight')}</span>
            </h2>
            <p className="text-gray-600 text-base md:text-lg max-w-xl">
              {t('products.subtitle')}
            </p>
          </div>
        </ScrollReveal>

        <ScrollStaggerContainer className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" staggerDelay={0.08} delay={0.2}>
          {products.map((product) => (
            <motion.div
              key={product.titleKey}
              className={`group relative rounded-2xl p-6 md:p-8 transition-all duration-500 ${
                product.featured
                  ? 'bg-gradient-to-br from-primary-500/10 to-primary-500/5 border-2 border-primary-500/30'
                  : 'bg-gray-50 border border-gray-200 hover:border-primary-300/50'
              }`}
              whileHover={{ y: -6 }}
            >
              {product.featured && (
                <div className="absolute -top-3 right-6 px-3 py-1 rounded-full bg-primary-500 text-white text-xs font-semibold animate-pulse-glow">
                  {t('products.popular')}
                </div>
              )}

              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-5 transition-all duration-500 ${
                product.featured
                  ? 'bg-primary-500 text-white'
                  : 'bg-red-50 text-red-600 group-hover:bg-primary-500 group-hover:text-white'
              }`}>
                <i className={`${product.icon} text-2xl`}></i>
              </div>

              <h3 className="font-heading text-lg font-semibold text-gray-900 mb-2">
                {t(product.titleKey)}
              </h3>

              <div className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                {t(product.priceKey)}
              </div>

              <p className="text-gray-600 text-sm leading-relaxed mb-6">
                {t(product.descKey)}
              </p>

              <Link
                to={product.href}
                className={`inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold transition-all duration-300 whitespace-nowrap ${
                  product.featured
                    ? 'bg-primary-500 text-white hover:bg-primary-600'
                    : 'bg-gray-900 text-white hover:bg-gray-800'
                }`}
              >
                {t(product.ctaKey)}
                <i className="ri-arrow-right-line"></i>
              </Link>
            </motion.div>
          ))}
        </ScrollStaggerContainer>
      </div>
    </section>
  );
}